import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import './CampaignsPanel.css';

// Promotional email, composed and sent from the admin portal.
//
// The shape of this screen is driven by one thing: a campaign cannot be recalled. So the path to
// the send button deliberately runs through seeing the thing — the preview renders the exact HTML
// that will be delivered, the recipient count is shown next to the button rather than buried, and
// sending a test to yourself sits between drafting and sending because a browser preview does not
// tell you what Gmail will do with it. The confirmation names the audience and the number.

const API = process.env.REACT_APP_API_URL;

function authHeaders() {
  return { Authorization: `Bearer ${localStorage.getItem('adminToken')}` };
}

const EMPTY_DRAFT = { subject: '', preheader: '', body: '', audience: 'all' };

function CampaignsPanel() {
  const [campaigns, setCampaigns] = useState([]);
  const [audiences, setAudiences] = useState({});
  const [counts, setCounts] = useState({});
  const [templates, setTemplates] = useState([]);
  const [suppressed, setSuppressed] = useState(0);
  const [batch, setBatch] = useState(null);

  const [selected, setSelected] = useState(null);
  const [draft, setDraft] = useState(EMPTY_DRAFT);
  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState('');

  const [digest, setDigest] = useState(null);
  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/campaigns`, { headers: authHeaders() });
      setCampaigns(data.campaigns || []);
      setAudiences(data.audiences || {});
      setCounts(data.counts || {});
      setTemplates(data.templates || []);
      setSuppressed(data.suppressed || 0);
      setBatch(data.batch || null);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // While a campaign is going out, the row is the only feedback there is — the send outlives the
  // request that started it, so progress has to be polled.
  useEffect(() => {
    const sending = campaigns.some((campaign) => campaign.status === 'sending');
    if (!sending) {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
      return undefined;
    }
    if (pollRef.current) return undefined;
    pollRef.current = setInterval(load, 4000);
    return undefined;
  }, [campaigns, load]);

  useEffect(() => () => { if (pollRef.current) clearInterval(pollRef.current); }, []);

  // What the monthly ranking would produce right now, without writing anything. Answers "why is
  // my signal not in it" without having to draft a campaign to find out.
  const checkDigest = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API}/api/admin/campaigns/monthly-digest/preview`, { headers: authHeaders() },
      );
      setDigest(data);
    } catch (err) {
      setDigest(null);
    }
  }, []);

  useEffect(() => { checkDigest(); }, [checkDigest]);

  const openDraft = (campaign) => {
    setSelected(campaign);
    setDraft({
      subject: campaign.subject,
      preheader: campaign.preheader,
      body: campaign.body,
      audience: campaign.audience,
    });
    setPreview(null);
    setNotice('');
  };

  const create = async (template) => {
    setBusy('create');
    try {
      const { data } = await axios.post(
        `${API}/api/admin/campaigns`,
        template
          ? { subject: template.subject, preheader: template.preheader, body: template.body, audience: 'all' }
          : { subject: 'Untitled campaign', preheader: '', body: '', audience: 'all' },
        { headers: authHeaders() },
      );
      await load();
      openDraft(data.campaign);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const save = async () => {
    if (!selected) return null;
    setBusy('save');
    try {
      const { data } = await axios.put(`${API}/api/admin/campaigns/${selected.id}`, draft, { headers: authHeaders() });
      setSelected(data.campaign);
      setNotice('Saved.');
      await load();
      return data.campaign;
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      return null;
    } finally {
      setBusy('');
    }
  };

  // Always saves first: previewing what is on screen rather than what is stored is how someone
  // ends up approving one message and sending another.
  const showPreview = async () => {
    if (!selected) return;
    const saved = await save();
    if (!saved) return;
    setBusy('preview');
    try {
      const { data } = await axios.get(`${API}/api/admin/campaigns/${selected.id}/preview`, { headers: authHeaders() });
      setPreview(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const sendTest = async () => {
    if (!selected || !testEmail.trim()) return;
    const saved = await save();
    if (!saved) return;
    setBusy('test');
    try {
      await axios.post(`${API}/api/admin/campaigns/${selected.id}/test`,
        { email: testEmail.trim() }, { headers: authHeaders() });
      setNotice(`Test sent to ${testEmail.trim()}. Read it in a real inbox before sending to the list.`);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const send = async () => {
    if (!selected) return;
    const saved = await save();
    if (!saved) return;
    const total = counts[draft.audience] ?? 0;
    // A native confirm rather than a styled dialog: this is the one action on this screen that
    // cannot be undone, and it names the number and the audience so the click is informed.
    const ok = window.confirm(
      `Send "${draft.subject}" to ${total} recipient${total === 1 ? '' : 's'}`
      + ` (${audiences[draft.audience] || draft.audience})?\n\n`
      + 'This cannot be recalled. Send yourself a test first if you have not.',
    );
    if (!ok) return;
    setBusy('send');
    try {
      const { data } = await axios.post(`${API}/api/admin/campaigns/${selected.id}/send`, {}, { headers: authHeaders() });
      setNotice(`Sending to ${data.recipients} recipients. Progress updates below.`);
      setSelected(null);
      setPreview(null);
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy('');
    }
  };

  const remove = async (campaign) => {
    if (!window.confirm(`Delete "${campaign.subject}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/campaigns/${campaign.id}`, { headers: authHeaders() });
      if (selected?.id === campaign.id) { setSelected(null); setPreview(null); }
      await load();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    }
  };

  const isDraft = selected && selected.status === 'draft';

  return (
    <div className="campaigns-panel">
      <div className="campaigns-head">
        <div>
          <h2>Promotional email</h2>
          <p className="campaigns-lead">
            Reach everyone whose address you hold — including people who bought a product and never
            made an account. Every message carries an unsubscribe link, and anyone who uses it is
            excluded from all future campaigns automatically.
          </p>
        </div>
        <div className="campaigns-stats">
          {Object.entries(audiences).map(([id, label]) => (
            <div key={id} className="campaigns-stat" title={label}>
              <strong>{counts[id] ?? '—'}</strong>
              <span>{label}</span>
            </div>
          ))}
          <div className="campaigns-stat">
            <strong>{suppressed}</strong>
            <span>Unsubscribed (never mailed)</span>
          </div>
        </div>
      </div>

      {error && <p className="campaigns-error">{error}</p>}
      {notice && <p className="campaigns-notice">{notice}</p>}

      <div className="campaigns-actions">
        <button type="button" className="campaigns-primary" disabled={busy === 'create'} onClick={() => create(null)}>
          New campaign
        </button>
        <button
          type="button"
          className="campaigns-ghost"
          disabled={busy === 'digest'}
          onClick={async () => {
            setBusy('digest');
            setError('');
            try {
              const { data } = await axios.post(
                `${API}/api/admin/campaigns/monthly-digest`, { audience: 'all' }, { headers: authHeaders() },
              );
              await load();
              openDraft(data.campaign);
              setNotice(`Drafted the ${data.period} top signals from ${data.signals.length} eligible signal(s). Preview it before sending.`);
            } catch (err) {
              setError(err.response?.data?.error || err.message);
            } finally {
              setBusy('');
            }
          }}
        >
          {busy === 'digest' ? 'Ranking…' : 'Draft this month’s top signals'}
        </button>
        {templates.map((template) => (
          <button key={template.id} type="button" className="campaigns-ghost" onClick={() => create(template)}>
            Start from: {template.label}
          </button>
        ))}
      </div>

      {digest && (
        <div className="campaigns-digest">
          <h3>Monthly top signals — {digest.period}</h3>
          <p className="campaigns-muted">
            Sent automatically once a month to everyone who has not unsubscribed, and drafted by
            hand with the button above. Only <strong>public, running</strong> signals qualify, and
            only past the marketplace bar: {digest.bar.daysLive} days live,
            {' '}{digest.bar.liveTrades} live trades, and at least {digest.bar.tradesInPeriod} closed
            trades in the month. Ranked by the month&apos;s return measured from the signal&apos;s own
            balance curve.
          </p>
          {digest.eligible === 0 ? (
            <p className="campaigns-muted">
              <strong>Nothing qualifies for {digest.period} yet</strong> — so no email would go out.
              An empty &ldquo;top signals&rdquo; message is worse than sending nothing.
            </p>
          ) : (
            <ol className="campaigns-digest-list">
              {digest.wouldInclude.map((entry) => (
                <li key={entry.id}>
                  <strong>{entry.name}</strong>
                  <span className="campaigns-muted">
                    {entry.symbol} · {entry.timeframe} ·{' '}
                    <span className={entry.monthReturn >= 0 ? 'campaigns-up' : 'campaigns-down'}>
                      {entry.monthReturn >= 0 ? '+' : ''}{entry.monthReturn.toFixed(2)}%
                    </span>{' '}
                    over {entry.monthTrades} trade{entry.monthTrades === 1 ? '' : 's'}
                  </span>
                </li>
              ))}
            </ol>
          )}
        </div>
      )}

      <div className="campaigns-body">
        <div className="campaigns-list">
          <h3>Campaigns</h3>
          {campaigns.length === 0 ? (
            <p className="campaigns-muted">No campaigns yet. Start from one of the drafts above.</p>
          ) : campaigns.map((campaign) => (
            <div
              key={campaign.id}
              className={`campaigns-row${selected?.id === campaign.id ? ' is-open' : ''}`}
            >
              <button type="button" className="campaigns-row-main" onClick={() => openDraft(campaign)}>
                <strong>{campaign.subject || 'Untitled'}</strong>
                <span className={`campaigns-status is-${campaign.status}`}>{campaign.status}</span>
                <span className="campaigns-muted">
                  {audiences[campaign.audience] || campaign.audience}
                  {campaign.status !== 'draft' && ` · ${campaign.sentCount} sent`}
                  {campaign.failedCount > 0 && ` · ${campaign.failedCount} failed`}
                </span>
                {campaign.status === 'sending' && campaign.recipients > 0 && (
                  <span className="campaigns-progress">
                    <span style={{ width: `${Math.round((campaign.sentCount / campaign.recipients) * 100)}%` }} />
                  </span>
                )}
              </button>
              {campaign.status !== 'sending' && (
                <button type="button" className="campaigns-delete" onClick={() => remove(campaign)}>Delete</button>
              )}
            </div>
          ))}
        </div>

        <div className="campaigns-editor">
          {!selected ? (
            <p className="campaigns-muted">Pick a campaign, or start a new one.</p>
          ) : (
            <>
              <h3>{isDraft ? 'Edit draft' : 'Sent campaign'}</h3>
              {!isDraft && (
                <p className="campaigns-muted">
                  This one has gone out, so it is read-only — it is the record of what people
                  actually received. Start a new campaign to send something similar.
                </p>
              )}

              <label className="campaigns-field">
                <span>Subject</span>
                <input
                  type="text"
                  value={draft.subject}
                  disabled={!isDraft}
                  onChange={(event) => setDraft({ ...draft, subject: event.target.value })}
                />
              </label>

              <label className="campaigns-field">
                <span>Preheader</span>
                <input
                  type="text"
                  value={draft.preheader}
                  disabled={!isDraft}
                  placeholder="The grey line shown after the subject in the inbox"
                  onChange={(event) => setDraft({ ...draft, preheader: event.target.value })}
                />
              </label>

              <label className="campaigns-field">
                <span>Audience</span>
                <select
                  value={draft.audience}
                  disabled={!isDraft}
                  onChange={(event) => setDraft({ ...draft, audience: event.target.value })}
                >
                  {Object.entries(audiences).map(([id, label]) => (
                    <option key={id} value={id}>{label} ({counts[id] ?? 0})</option>
                  ))}
                </select>
              </label>

              <label className="campaigns-field">
                <span>
                  Body — <strong>##</strong> heading, <strong>**bold**</strong>, <strong>- </strong>bullet,
                  {' '}<strong>[text](link)</strong>. Write <strong>SITE_URL</strong> and it becomes your site address.
                </span>
                <textarea
                  rows={16}
                  value={draft.body}
                  disabled={!isDraft}
                  onChange={(event) => setDraft({ ...draft, body: event.target.value })}
                />
              </label>

              {isDraft && (
                <>
                  <div className="campaigns-actions">
                    <button type="button" className="campaigns-ghost" disabled={busy === 'save'} onClick={save}>
                      {busy === 'save' ? 'Saving…' : 'Save draft'}
                    </button>
                    <button type="button" className="campaigns-ghost" disabled={busy === 'preview'} onClick={showPreview}>
                      {busy === 'preview' ? 'Rendering…' : 'Preview'}
                    </button>
                  </div>

                  <div className="campaigns-test">
                    <label className="campaigns-field">
                      <span>Send a test to yourself first</span>
                      <input
                        type="email"
                        value={testEmail}
                        placeholder="you@example.com"
                        onChange={(event) => setTestEmail(event.target.value)}
                      />
                    </label>
                    <button type="button" className="campaigns-ghost" disabled={busy === 'test' || !testEmail.trim()} onClick={sendTest}>
                      {busy === 'test' ? 'Sending…' : 'Send test'}
                    </button>
                  </div>

                  <div className="campaigns-send">
                    <button type="button" className="campaigns-danger" disabled={busy === 'send'} onClick={send}>
                      {busy === 'send' ? 'Starting…' : `Send to ${counts[draft.audience] ?? 0} recipients`}
                    </button>
                    <p className="campaigns-muted">
                      Goes out in batches of {batch?.size ?? 20} with a {Math.round((batch?.pauseMs ?? 4000) / 1000)}s
                      pause between them, so the send does not look like a burst to your mail host.
                      Anyone who unsubscribes while it runs is dropped from the remaining batches.
                    </p>
                  </div>
                </>
              )}

              {preview && (
                <div className="campaigns-preview">
                  <h4>
                    Preview — {preview.recipients} recipient{preview.recipients === 1 ? '' : 's'} ({preview.audienceLabel})
                  </h4>
                  {preview.sample?.length > 0 && (
                    <p className="campaigns-muted">First few: {preview.sample.join(', ')}</p>
                  )}
                  {/* Rendered in an iframe so the email's own styles cannot leak into the admin
                      page, and so what is shown is genuinely the document that gets sent. */}
                  <iframe title="Campaign preview" className="campaigns-frame" srcDoc={preview.html} />
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default CampaignsPanel;
